import { FieldValue } from "backend/datainput";
import { ICardElementWithChildren } from "backend/model/model";
import * as types from "client-api-workflow/types";
import ValueInputForm from "design/components/forms/ValueInputForm";
import { t } from "i18next";
import React from "react";
import { getDateFromMEAValue } from "utils/utils";
import * as validators from "./validator";
import validateInputForm from "./validator/validateInputForm";

const InitiativeInputForm = (props: {
  initiative: ICardElementWithChildren;
  childElements: ICardElementWithChildren[];
  comments?: types.WFComment[];
  attachments?: types.FileValue[];
  translatedName: string;
  onSubmit?: (values: FieldValue[]) => void;
  isReadOnly?: boolean;
}) => {
  const { initiative, childElements, attachments, comments } = props;

  const [milestoneValues, setMilestoneValues] = React.useState<FieldValue[]>(
    []
  );

  const milestones = React.useMemo(() => {
    return (childElements || []).filter(
      (item) => item.typename === "Milestone"
    );
  }, [childElements]);

  // Merge logic: Ensures edits are reflected in the UI immediately
  const mergedChildElements = React.useMemo(() => {
    return childElements.map((child) => {
      if (child.typename !== "Milestone") return child;

      const newChild = { ...child };

      const edits = milestoneValues.filter(
        (v) => v.field.element?.id === child.id
      );

      edits.forEach((edit) => {
        const series = edit.series;
        if (series) {
          (newChild as any)[`${series}_prettyvalue`] = edit.value;
          (newChild as any)[`${series}_valuewithoutsymbol`] = edit.value;
          (newChild as any)[series] = edit.value;
        }
      });

      return newChild;
    });
  }, [childElements, milestoneValues]);

  const getDateValue = (value?: Date) => {
    if (!value) return "";
    return `${value.getFullYear()}-${(value.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${value.getDate().toString().padStart(2, "0")}`;
  };

  const handleMilestoneChange = (data: {
    value: string;
    id: string;
    name: string;
  }) => {
    const { value, id, name } = data;

    setMilestoneValues((prev) => {
      const existingIndex = prev.findIndex(
        (p) => p.field.element?.id === id && p.series === name
      );

      const targetElement = milestones.find((m) => m.id === id);

      const newField: FieldValue = {
        id: `${id}_${name}`,
        series: name,
        value: value,
        field: {
          element: targetElement,
          inputType: name === "ACTUAL_END_DATE" ? "date" : "string",
        },
      };

      if (existingIndex >= 0) {
        const newArr = [...prev];
        newArr[existingIndex] = newField;
        return newArr;
      } else {
        return [...prev, newField];
      }
    });
  };

  return (
    <>
      <ValueInputForm
        key={initiative?.workflowInstance?.wfInstanceId}
        element={initiative!}
        childElements={mergedChildElements}
        onSubElementChange={handleMilestoneChange}
        buttons={[
          {
            tag: "submit",
            style: "submit",
            text: t("workflowinput.form.Submit").toString(),
            onClick: (values) => {
              if (props.onSubmit) {
                const submitValues = values.filter((v) => !v.field.isDisabled);
                const allValues = [...submitValues, ...milestoneValues].map(
                  (v) => {
                    if (v.field.element) {
                      v.field.element.workflowInstance =
                        initiative.workflowInstance;
                    }
                    return v;
                  }
                );

                props.onSubmit(allValues);
              }
            },
          },
        ]}
        onIntermediateChange={(values) => {}}
        fields={[
          {
            title: t("workflowinput.form.END_DATE").toString(),
            series: "END_DATE",
            element: initiative,
            inputType: "datetime",
            initialValue: initiative.END_DATE
              ? getDateValue(getDateFromMEAValue(initiative.END_DATE))
              : undefined,
            isDisabled: true,
          },
          {
            title: t("workflowinput.form.COMPLETION_PERCENTAGE").toString(),
            series: "COMPLETION_PERCENTAGE",
            element: initiative,
            inputType: "string",
            unitSeries: "COMPLETION_PERCENTAGE_unit",
            initialValue: initiative.COMPLETION_PERCENTAGE,
            isDisabled: true,
          },
          {
            title: t("workflowinput.form.EXPENDITURE").toString(),
            series: "EXPENDITURE",
            element: initiative,
            inputType: "string",
            unitSeries: "EXPENDITURE_unit",
            initialValue: initiative.EXPENDITURE,
            isDisabled: true,
          },
          {
            title: t("workflowinput.form.Attachments"),
            element: initiative,
            inputType: "attachments" as "attachments",
            initialFiles: attachments,
            isDisabled: props.isReadOnly,
            validate: (state) => {
              if (!state.field.files || state.field.files?.length < 1) {
                return {
                  success: false,
                  errorMessages: [
                    t("workflowinput.form.errors.Attachments can not be empty"),
                  ],
                };
              } else
                return {
                  success: true,
                  errorMessages: [],
                };
            },
          },
          {
            title: t("workflowinput.form.Actions and Recommendations"),
            element: initiative,
            inputType: "comment" as "comment",
            comments: comments,
            isDisabled: props.isReadOnly,
            validate: (state) => {
              if (!state.field.value || state.field.value?.length < 1) {
                return {
                  success: false,
                  errorMessages: [
                    t(
                      "workflowinput.form.errors.Actions and Recommendations can not be empty"
                    ),
                  ],
                };
              } else
                return {
                  success: true,
                  errorMessages: [],
                };
            },
            initialValue:
              comments && comments[0] ? comments[0].description : "",
          },
        ]}
      />
    </>
  );
};

export default InitiativeInputForm;
